import React, { useEffect, useRef } from 'react';
import { useTheme } from '../../contexts/ThemeContext';

const ParticleBackground = ({ shouldAnimate = true }) => {
  const canvasRef = useRef(null);
  const { theme } = useTheme();
  const isDarkMode =
    theme === 'dark' ||
    (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  const particlesRef = useRef([]);
  const animationFrameIdRef = useRef(null);
  
  // Change to use a slow speed factor instead of stopping completely
  const velocityFactorRef = useRef(shouldAnimate ? 1.0 : 0.05);
  const targetVelocityRef = useRef(shouldAnimate ? 1.0 : 0.05);
  
  useEffect(() => {
    // Update target velocity to slow down instead of stopping
    targetVelocityRef.current = shouldAnimate ? 1.0 : 0.05;
  }, [shouldAnimate]);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Set canvas size
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    
    window.addEventListener('resize', resizeCanvas);
    resizeCanvas();
    
    // Particle class
    class Particle {
      constructor() {
        this.reset();
      }
      
      reset() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.size = Math.random() * 0.5 + 0.5;
        this.speedX = (Math.random() - 0.5) * 1;
        this.speedY = (Math.random() - 0.5) * 1;
        this.opacity = Math.random() * 0.5 + 0.1;
      }
      
      update(velocityFactor) {
        // Apply velocity factor to make movement smooth
        this.x += this.speedX * velocityFactor;
        this.y += this.speedY * velocityFactor;
        
        // Reset particle if it goes off screen
        if (this.x < 0 || this.x > canvas.width || this.y < 0 || this.y > canvas.height) {
          this.reset();
        }
      }
      
      draw() {
        ctx.fillStyle = isDarkMode 
          ? `rgba(255, 255, 255, ${this.opacity})` 
          : `rgba(0, 0, 0, ${this.opacity})`;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.closePath();
        ctx.fill();
        
        // Add a sharper edge with a stroke
        ctx.strokeStyle = isDarkMode 
          ? `rgba(255, 255, 255, ${this.opacity * 1.2})` 
          : `rgba(0, 0, 0, ${this.opacity * 1.2})`;
        ctx.lineWidth = 0.5;
        ctx.stroke();
      }
    }
    
    // Initialize particles
    const initParticles = () => {
      const particles = [];
      const particleCount = Math.min(window.innerWidth, window.innerHeight) * 0.30;
      for (let i = 0; i < particleCount; i++) {
        particles.push(new Particle());
      }
      particlesRef.current = particles;
      return particles;
    };
    
    // Animation loop
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Smoothly transition velocity factor
      const currentVelocity = velocityFactorRef.current;
      const targetVelocity = targetVelocityRef.current;
      
      // Ease the velocity factor towards the target
      if (currentVelocity !== targetVelocity) {
        if (currentVelocity < targetVelocity) {
          velocityFactorRef.current = Math.min(currentVelocity + 0.009, targetVelocity);
        } else {
          velocityFactorRef.current = Math.max(currentVelocity - 0.009, targetVelocity);
        }
      }
      
      const particles = particlesRef.current;
      
      // Draw connections between particles
      ctx.strokeStyle = isDarkMode 
        ? 'rgba(255, 255, 255, 0.05)' 
        : 'rgba(0, 0, 0, 0.05)';
      ctx.lineWidth = 0.5;
      
      for (let i = 0; i < particles.length; i++) {
        for (let j = i; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          if (distance < 100) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }
      
      // Update and draw particles with current velocity factor
      particles.forEach(particle => {
        particle.update(velocityFactorRef.current);
        particle.draw();
      });
      
      animationFrameIdRef.current = requestAnimationFrame(animate);
    };
    
    const particles = initParticles();
    particlesRef.current = particles;
    animate();
    
    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (animationFrameIdRef.current) {
        cancelAnimationFrame(animationFrameIdRef.current);
      }
    };
  }, [isDarkMode]);
  
  return (
    <canvas 
      ref={canvasRef} 
      className="absolute top-0 left-0 w-full h-full z-0 theme-app-bg"
    />
  );
};

export default ParticleBackground; 